/* eslint-disable import/no-named-as-default */
import './styles.scss';
import React from 'react';
import PropTypes from 'prop-types';
import track from 'react-tracking';
import { Modal } from '@edx/paragon';
import { Close } from '@edx/paragon/icons';
import { FormattedMessage } from 'gatsby-plugin-react-intl';
import { WebviewContext } from '@utils/context';
import { MIT_SDS_PROGRAM_1_UUID, MIT_SDS_PROGRAM_2_UUID } from '@build-utils/pages/programs';
import programShape from '@prospectus/common/types/programShape';

import ProgramPursueButton from '../ProgramPursueButton';

// If this one of the MIT Statistics and Data Science programs, we need to
// hardcode some text instead of relying on the data from Discovery. Overriding
// here instead of programs.json because we don’t want to override the title
// everywhere; just in this modal.
const REPLACEMENT_DATA = {
  [MIT_SDS_PROGRAM_1_UUID]: {
    title: (
      <FormattedMessage
        id="prospectus.program_track_modal.social_sciences_track_title"
        description="Title for Social Sciences track program"
        defaultMessage="Social Sciences Track"
      />
    ),
    description: (
      <FormattedMessage
        id="prospectus.program_track_modal.social_sciences_track_description"
        description="Description for Social Sciences track program"
        defaultMessage="Learn probability, statistics, and machine learning, and apply your knowledge to address complex societal challenges."
      />
    ),
  },
  [MIT_SDS_PROGRAM_2_UUID]: {
    title: (
      <FormattedMessage
        id="prospectus.program_track_modal.general_track_title"
        description="Title for general track program"
        defaultMessage="General Track"
      />
    ),
    description: (
      <FormattedMessage
        id="prospectus.program_track_modal.general_track_description"
        description="Description for general track program"
        defaultMessage="Learn probability, statistics, machine learning, and common models to analyze data, from image recognition to networks and time series."
      />
    ),
  },
};

function ProgramTrackModal({
  open,
  onClose,
  parentSelector,
  passedEmailOptIn,
  linkedProgram,
  linkedProgramCourses,
  ...program
}) {
  const renderProgramOptions = (prog) => {
    const enrollButtonId = `enroll-btn-${prog.uuid}`;

    const title = REPLACEMENT_DATA[prog.uuid]
      ? REPLACEMENT_DATA[prog.uuid].title
      : prog.title;

    const description = REPLACEMENT_DATA[prog.uuid]
      ? REPLACEMENT_DATA[prog.uuid].description
      : prog.subtitle;

    return (
      <WebviewContext.Consumer>
        {(webview) => (
          <div key={prog.title} className="program-track-card">
            <div className="program-text-container mb-5">
              <p className="price">{prog.formattedPrice}</p>
              <h4 aria-level="3">{title}</h4>
              <p>{description}</p>
            </div>
            <ProgramPursueButton
              {...prog}
              id={enrollButtonId}
              hideEmailOptIn
              passedEmailOptIn={passedEmailOptIn}
              screenReaderText={title}
              webview={webview}
            />
          </div>
        )}
      </WebviewContext.Consumer>
    );
  };

  // This will not be displayed but is required by Modal and used for
  // accessibility
  const modalTitle = (
    <FormattedMessage
      id="prospectus.program_track_modal.title"
      description="Label for track seleciton modal"
      defaultMessage="Program track selection modal."
    />
  );

  const programOptions = [linkedProgram, program].map(prog => renderProgramOptions(prog, passedEmailOptIn));

  return (
    <Modal
      open={open}
      title={modalTitle}
      body={(
        <>
          <button id="program-track-modal-close-btn" className="close" onClick={onClose} type="button">
            <Close
              id="icon-close-mobile-filter-menu"
            />
            <div className="sr-only">
              <FormattedMessage
                id="prospectus.program_track_modal.close"
                description="Label for button to close program track modal."
                defaultMessage="Close program track modal."
              />
            </div>
          </button>
          <div className="program-track-modal-content">
            <h3 aria-level="2" className="mb-5">
              <FormattedMessage
                id="prospectus.program_track_modal.header"
                description="Heading for the program track modal"
                defaultMessage="Choose a track for the {partner} {programTitle} {programType} Program"
                values={{
                  partner: program.authoringOrganizations[0].key,
                  programTitle: program.title,
                  programType: <>{program.type}<sup>®</sup></>,
                }}
              />
            </h3>
            <div className="program-tracks-container">
              {programOptions}
            </div>
          </div>
        </>
        )}
      onClose={onClose}
      parentSelector={parentSelector}
      dialogClassName="program-track-modal modal-lg"
      renderHeaderCloseButton={false}
      renderDefaultCloseButton={false}
    />
  );
}

ProgramTrackModal.propTypes = {
  uuid: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  formattedPrice: PropTypes.string.isRequired,
  authoringOrganizations: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    key: PropTypes.string,
  })).isRequired,
  type: PropTypes.string.isRequired,
  linkedProgram: PropTypes.shape(programShape).isRequired,
  linkedProgramCourses: PropTypes.arrayOf(PropTypes.shape()),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  parentSelector: PropTypes.string,
  passedEmailOptIn: PropTypes.bool,
};

ProgramTrackModal.defaultProps = {
  parentSelector: undefined,
  passedEmailOptIn: undefined,
  linkedProgramCourses: undefined,
};

export default track({
  component: 'program-track-modal',
})(ProgramTrackModal);
